import React, { useState, useRef } from 'react';
import { 
  FileText, 
  Upload, 
  Trash2, 
  Eye, 
  Download, 
  Receipt, 
  CreditCard, 
  FileCode, 
  Plus, 
  CheckCircle2, 
  AlertCircle,
  FileSpreadsheet,
  FileCheck
} from 'lucide-react';
import { ContaAnexo, TipoAnexoId } from '../../types/financeiro';
import { storageService } from '../../services/storageService';

export interface PendingAnexo {
  id?: number;
  tipo_anexo_id: number;
  tipo_nome: string;
  descricao: string;
  arquivo: string; // URL or Data URI
  nome_arquivo?: string;
  tamanho?: number;
  isExisting?: boolean;
}

interface ContaAnexosManagerProps {
  anexos: PendingAnexo[];
  onChange: (anexos: PendingAnexo[]) => void;
  isReadOnly?: boolean;
  onPreviewAnexo?: (anexo: PendingAnexo) => void;
}

const TIPO_ANEXO_OPTIONS = [
  { id: TipoAnexoId.BOLETO, nome: 'Boleto Bancário', icon: FileCode, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: TipoAnexoId.NOTA_FISCAL, nome: 'Nota Fiscal (NF / Danfe)', icon: Receipt, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { id: TipoAnexoId.COMPROVANTE, nome: 'Comprovante de Pagamento', icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: TipoAnexoId.RECIBO, nome: 'Recibo', icon: FileCheck, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: TipoAnexoId.FATURA, nome: 'Fatura', icon: CreditCard, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { id: TipoAnexoId.OUTROS, nome: 'Outros Documentos', icon: FileText, color: 'bg-slate-50 text-slate-700 border-slate-200' },
];

export const ContaAnexosManager: React.FC<ContaAnexosManagerProps> = ({
  anexos,
  onChange,
  isReadOnly = false,
  onPreviewAnexo,
}) => {
  const [selectedTipoId, setSelectedTipoId] = useState<number>(TipoAnexoId.BOLETO);
  const [customDescricao, setCustomDescricao] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgressText, setUploadProgressText] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getTipoInfo = (tipoId: number) => {
    return TIPO_ANEXO_OPTIONS.find(t => t.id === tipoId) || {
      id: tipoId,
      nome: 'Documento',
      icon: FileText,
      color: 'bg-slate-50 text-slate-700 border-slate-200'
    };
  };

  const handleFiles = async (files: FileList | File[]) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);

    const newAnexosList: PendingAnexo[] = [...anexos];
    const totalFiles = files.length;

    for (let i = 0; i < totalFiles; i++) {
      const file = files[i];
      setUploadProgressText(`Enviando arquivo ${i + 1} de ${totalFiles}: ${file.name}...`);

      if (file.size > 25 * 1024 * 1024) {
        alert(`Arquivo "${file.name}" ultrapassa o limite de 25MB.`);
        continue;
      }

      const fileNameLower = file.name.toLowerCase();
      // Auto-detect document type if not explicitly selected or if uploading multiple files
      let detectedTipoId = selectedTipoId;
      if (totalFiles > 1) {
        if (fileNameLower.includes('boleto') || fileNameLower.includes('bol')) {
          detectedTipoId = TipoAnexoId.BOLETO;
        } else if (fileNameLower.includes('nf') || fileNameLower.includes('danfe') || fileNameLower.includes('nota')) {
          detectedTipoId = TipoAnexoId.NOTA_FISCAL;
        } else if (fileNameLower.includes('comp') || fileNameLower.includes('pag') || fileNameLower.includes('pix')) {
          detectedTipoId = TipoAnexoId.COMPROVANTE;
        } else if (fileNameLower.includes('recibo')) {
          detectedTipoId = TipoAnexoId.RECIBO;
        } else if (fileNameLower.includes('fat')) {
          detectedTipoId = TipoAnexoId.FATURA;
        }
      }

      const tipoInfo = getTipoInfo(detectedTipoId);
      let fileUrl = '';

      try {
        const { publicUrl } = await storageService.uploadFile(file, 'comprovantes');
        fileUrl = publicUrl;
      } catch (err) {
        console.warn('Falha no upload direto para bucket, usando fallback local Base64:', err);
        fileUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => resolve('');
          reader.readAsDataURL(file);
        });
      }

      if (fileUrl) {
        const desc = totalFiles === 1 && customDescricao.trim() 
          ? customDescricao.trim() 
          : file.name;

        newAnexosList.push({
          tipo_anexo_id: detectedTipoId,
          tipo_nome: tipoInfo.nome,
          descricao: desc,
          arquivo: fileUrl,
          nome_arquivo: file.name,
          tamanho: file.size,
          isExisting: false
        });
      }
    }

    onChange(newAnexosList);
    setCustomDescricao('');
    setIsUploading(false);
    setUploadProgressText('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveAnexo = (index: number) => {
    const updated = anexos.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleChangeTipo = (index: number, newTipoId: number) => {
    const updated = [...anexos];
    const tipoInfo = getTipoInfo(newTipoId);
    updated[index] = {
      ...updated[index],
      tipo_anexo_id: newTipoId,
      tipo_nome: tipoInfo.nome
    };
    onChange(updated);
  };

  const handleChangeDescricao = (index: number, newDesc: string) => {
    const updated = [...anexos];
    updated[index] = {
      ...updated[index],
      descricao: newDesc
    };
    onChange(updated);
  };

  const handleDownload = (anexo: PendingAnexo) => {
    const link = document.createElement('a');
    link.href = anexo.arquivo;
    link.download = anexo.nome_arquivo || anexo.descricao || 'documento';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-3">
      {/* Header with Quick Type Selector */}
      {!isReadOnly && (
        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-bold text-slate-700">
                1. Selecione o Tipo Padrão do Próximo Anexo:
              </label>
              <span className="text-[11px] text-slate-500 font-medium">
                (Você pode alterar o tipo individual de cada arquivo depois)
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TIPO_ANEXO_OPTIONS.map((tipo) => {
                const Icon = tipo.icon;
                const isSelected = selectedTipoId === tipo.id;
                return (
                  <button
                    key={`tipo-btn-${tipo.id}`}
                    type="button"
                    onClick={() => setSelectedTipoId(tipo.id)}
                    className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-all cursor-pointer text-xs font-semibold ${
                      isSelected
                        ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-emerald-400' : 'text-slate-500'}`} />
                    <span className="truncate">{tipo.nome}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <input
              type="text"
              value={customDescricao}
              onChange={(e) => setCustomDescricao(e.target.value)}
              placeholder="Descrição personalizada (opcional, ex: NF 1234, Boleto Parcela 1)"
              className="flex-1 px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:outline-hidden"
            />
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer whitespace-nowrap"
            >
              <Upload className="w-3.5 h-3.5" />
              {isUploading ? 'Enviando...' : 'Selecionar Arquivo(s)'}
            </button>
          </div>

          {/* Drag & Drop Zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                handleFiles(e.dataTransfer.files);
              }
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer ${
              isDragOver ? 'border-emerald-500 bg-emerald-50/50' : 'border-slate-300 hover:border-slate-400 bg-white'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleFiles(e.target.files);
                }
              }}
            />
            {isUploading ? (
              <div className="py-2 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto mb-1.5"></div>
                <p className="text-xs font-bold text-slate-800">{uploadProgressText || 'Enviando arquivos...'}</p>
                <p className="text-[11px] text-slate-500">Por favor, aguarde o envio ser concluído.</p>
              </div>
            ) : (
              <>
                <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                <p className="text-xs font-semibold text-slate-700">
                  Arraste e solte múltiplos arquivos aqui ou clique para selecionar
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Suporta upload de múltiplos arquivos ao mesmo tempo (Boleto, Nota Fiscal, Comprovante, Recibos) em PDF ou Imagem
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lista de Documentos Anexados */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-700">
            Documentos Anexados ({anexos.length}):
          </span>
          {anexos.length > 0 && (
            <span className="text-[11px] text-slate-500">
              {anexos.length === 1 ? '1 documento anexado' : `${anexos.length} documentos anexados`}
            </span>
          )}
        </div>

        {anexos.length === 0 ? (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
            <p className="text-xs text-slate-500">Nenhum documento anexado a esta conta ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2.5">
            {anexos.map((anexo, idx) => {
              const tipoInfo = getTipoInfo(anexo.tipo_anexo_id);
              const Icon = tipoInfo.icon;
              return (
                <div
                  key={`anexo-item-${idx}-${anexo.id || 'new'}-${anexo.arquivo?.slice(-10)}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all shadow-2xs gap-3"
                >
                  <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                    <div className={`p-2 rounded-lg border flex-shrink-0 mt-0.5 sm:mt-0 ${tipoInfo.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isReadOnly ? (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${tipoInfo.color}`}>
                            {anexo.tipo_nome || tipoInfo.nome}
                          </span>
                        ) : (
                          <select
                            value={anexo.tipo_anexo_id}
                            onChange={(e) => handleChangeTipo(idx, Number(e.target.value))}
                            className="text-[11px] font-bold px-2 py-0.5 rounded-md border border-slate-300 bg-slate-50 text-slate-800 cursor-pointer focus:ring-1 focus:ring-slate-900"
                          >
                            {TIPO_ANEXO_OPTIONS.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.nome}
                              </option>
                            ))}
                          </select>
                        )}

                        {isReadOnly ? (
                          <p className="text-xs font-bold text-slate-800 truncate" title={anexo.descricao || anexo.nome_arquivo}>
                            {anexo.descricao || anexo.nome_arquivo || 'Documento Anexo'}
                          </p>
                        ) : (
                          <input
                            type="text"
                            value={anexo.descricao || ''}
                            onChange={(e) => handleChangeDescricao(idx, e.target.value)}
                            placeholder="Identificação do documento..."
                            className="text-xs font-semibold px-2 py-0.5 rounded border border-slate-200 bg-white text-slate-800 focus:border-slate-400 min-w-[140px] flex-1"
                          />
                        )}
                      </div>

                      {anexo.nome_arquivo && anexo.nome_arquivo !== anexo.descricao && (
                        <p className="text-[11px] text-slate-400 truncate">
                          Arquivo original: {anexo.nome_arquivo}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-1.5 flex-shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                    {onPreviewAnexo ? (
                      <button
                        type="button"
                        onClick={() => onPreviewAnexo(anexo)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        title="Visualizar documento"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Ver</span>
                      </button>
                    ) : (
                      <a
                        href={anexo.arquivo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        title="Abrir documento em nova aba"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Abrir</span>
                      </a>
                    )}

                    <button
                      type="button"
                      onClick={() => handleDownload(anexo)}
                      className="p-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer"
                      title="Baixar arquivo"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>

                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => handleRemoveAnexo(idx)}
                        className="p-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-lg transition-colors cursor-pointer"
                        title="Remover este anexo"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
